import apiClient from './api';

export interface VideoMetadata {
  duration?: number;
  format?: string;
  codec_video?: string;
  codec_audio?: string;
  resolution_width?: number;
  resolution_height?: number;
  bitrate?: number;
  frame_rate?: number;
  aspect_ratio?: string;
  custom_metadata?: Record<string, any>;
}

export interface Video extends VideoMetadata {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  source_type: 'upload' | 'remote' | 'onestudio' | 'ottstudio';
  remote_url?: string;
  file_path?: string;
  onestudio_video_id?: number; // Original 1Studio video ID
  ott_studio_video_id?: number; // Original OTT-Studio video ID
  storage_key?: string;
  file_size?: number;
  thumbnail_path?: string; // Legacy
  thumbnail_preview_url?: string; // 320px thumbnail
  thumbnail_preview_key?: string;
  thumbnail_full_url?: string; // 1280px thumbnail
  thumbnail_full_key?: string;
  status: 'processing' | 'ready' | 'error';
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface UploadSession {
  upload_id: string;
  storage_key: string;
  chunk_size: number;
  max_chunks: number;
}

export interface UploadProgress {
  uploadId: string;
  totalChunks: number;
  uploadedChunks: number;
  progress: number; // 0-100
  currentChunk?: number;
  error?: string;
}

class VideoService {
  /**
   * Create a video (supports all source types: upload, remote, onestudio, ottstudio)
   */
  async createVideo(data: {
    name: string;
    description?: string;
    source_type: 'upload' | 'remote' | 'onestudio' | 'ottstudio';
    remote_url?: string;
    duration?: number;
    thumbnail_preview_url?: string;
    onestudio_video_id?: number;
    ott_studio_video_id?: number;
  }): Promise<Video> {
    const response = await apiClient.post('/videos/create', data);
    return response.data.data.video;
  }

  /**
   * Add a remote video from URL
   */
  async addRemoteVideo(data: {
    name: string;
    description?: string;
    remote_url: string;
  }): Promise<{ video: Video; metadata_extracted: boolean }> {
    const response = await apiClient.post('/videos/add-remote', data);
    return response.data.data;
  }

  /**
   * Initialize multipart upload
   */
  async initializeUpload(data: {
    name: string;
    file_size: number;
    mime_type: string;
    description?: string;
  }): Promise<UploadSession> {
    const response = await apiClient.post('/videos/upload/init', data);
    return response.data.data;
  }

  /**
   * Upload a single chunk
   */
  async uploadChunk(
    uploadId: string,
    partNumber: number,
    chunk: Blob
  ): Promise<{ etag: string; part_number: number; size: number }> {
    const formData = new FormData();
    formData.append('upload_id', uploadId);
    formData.append('part_number', partNumber.toString());
    formData.append('chunk', chunk);

    const response = await apiClient.post('/videos/upload/chunk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  /**
   * Complete multipart upload
   */
  async completeUpload(data: {
    upload_id: string;
    name: string;
    description?: string;
    custom_metadata?: Record<string, any>;
  }): Promise<{ video: Video; metadata_extracted: boolean }> {
    const response = await apiClient.post('/videos/upload/complete', data);
    return response.data.data;
  }

  /**
   * Abort upload
   */
  async abortUpload(uploadId: string): Promise<void> {
    await apiClient.post('/videos/upload/abort', { upload_id: uploadId });
  }

  /**
   * List all videos with pagination and filters
   */
  async listVideos(params?: {
    page?: number;
    limit?: number;
    search?: string;
    source_type?: 'upload' | 'remote' | 'onestudio' | 'ottstudio';
    status?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<{
    videos: Video[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await apiClient.get('/videos', { params });
    return response.data.data;
  }

  /**
   * Get single video by ID
   */
  async getVideo(id: string): Promise<Video> {
    const response = await apiClient.get(`/videos/${id}`);
    return response.data.data.video;
  }

  /**
   * Update video metadata
   */
  async updateVideo(
    id: string,
    data: {
      name?: string;
      description?: string;
      tags?: string[];
      custom_metadata?: Record<string, any>;
    }
  ): Promise<Video> {
    const response = await apiClient.put(`/videos/${id}`, data);
    return response.data.data.video;
  }

  /**
   * Delete video (soft delete)
   */
  async deleteVideo(id: string): Promise<void> {
    await apiClient.delete(`/videos/${id}`);
  }

  /**
   * Bulk delete videos
   */
  async bulkDeleteVideos(videoIds: string[]): Promise<{
    deleted: number;
    failed: number;
  }> {
    const response = await apiClient.post('/videos/bulk/delete', { video_ids: videoIds });
    return response.data.data;
  }

  /**
   * Get video statistics
   */
  async getVideoStats(): Promise<{
    total_videos: number;
    total_size: number;
    total_duration: number;
    by_source_type: Record<string, number>;
    by_status: Record<string, number>;
  }> {
    const response = await apiClient.get('/videos/stats');
    return response.data.data;
  }

  /**
   * Get playback URL for a video (presigned URL for uploads, remote URL for remote videos)
   */
  async getPlaybackUrl(id: string): Promise<{
    playback_url: string;
    video_id: string;
    source_type: 'upload' | 'remote';
    expires_in: number | null;
  }> {
    const response = await apiClient.get(`/videos/${id}/playback-url`);
    return response.data.data;
  }

  /**
   * Upload a file with chunking and progress tracking
   */
  async uploadFile(
    file: File,
    metadata: { name: string; description?: string },
    onProgress?: (progress: UploadProgress) => void
  ): Promise<Video> {
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    try {
      // 1. Initialize upload
      const session = await this.initializeUpload({
        name: metadata.name,
        file_size: file.size,
        mime_type: file.type,
        description: metadata.description,
      });

      // 2. Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        await this.uploadChunk(session.upload_id, i + 1, chunk);

        // Report progress
        if (onProgress) {
          onProgress({
            uploadId: session.upload_id,
            totalChunks,
            uploadedChunks: i + 1,
            progress: Math.round(((i + 1) / totalChunks) * 100),
            currentChunk: i + 1,
          });
        }
      }

      // 3. Complete upload
      const result = await this.completeUpload({
        upload_id: session.upload_id,
        name: metadata.name,
        description: metadata.description,
      });

      return result.video;
    } catch (error: any) {
      // Try to abort if upload fails
      try {
        // await this.abortUpload(session.upload_id); // session might not exist
      } catch {}

      throw error;
    }
  }
}

export default new VideoService();
